import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import type { ToolPresetMask } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT, type Translations } from '@/shared/i18n'
import {
  rejectTxtifyToolOperation,
  runTxtifyToolOperation,
  TXTIFY_ANALYTICS_TOOLS,
} from '@/shared/analytics/tool-operations'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import {
  CHARACTER_SET_ORDER,
  createPasswordGeneratorState,
  generatePassword,
  LENGTH_COMMIT_DELAY_MS,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  PasswordGenerationError,
  passwordGeneratorReducer,
  selectedCharacterSets,
  type CharacterSetKey,
  type GeneratedPassword,
  type PasswordSettings,
} from '@/pages/generator-parolej/password-generator'

function strengthKey(entropyBits: number): keyof Translations['passwordGenerator']['strength'] {
  if (entropyBits < 45) return 'weak'
  if (entropyBits < 70) return 'fair'
  if (entropyBits < 100) return 'strong'
  return 'excellent'
}

function strengthClass(strength: keyof Translations['passwordGenerator']['strength']): string {
  switch (strength) {
    case 'weak':
      return 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100'
    case 'fair':
      return 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100'
    case 'strong':
      return 'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100'
    case 'excellent':
      return 'border-cyan-300 bg-cyan-50 text-cyan-950 dark:border-cyan-800 dark:bg-cyan-950/35 dark:text-cyan-100'
  }
}

function characterSetLabel(
  key: CharacterSetKey,
  t: Translations,
): string {
  switch (key) {
    case 'uppercase':
      return t.passwordGenerator.uppercase
    case 'lowercase':
      return t.passwordGenerator.lowercase
    case 'numbers':
      return t.passwordGenerator.numbers
    case 'symbols':
      return t.passwordGenerator.symbols
  }
}

function passwordPresetMask(settings: PasswordSettings): ToolPresetMask {
  return (
    (settings.uppercase ? 1 : 0)
    | (settings.lowercase ? 2 : 0)
    | (settings.numbers ? 4 : 0)
    | (settings.symbols ? 8 : 0)
    | (settings.excludeAmbiguous ? 16 : 0)
  ) as ToolPresetMask
}

function generationErrorCode(error: unknown): 'invalid_input' | 'processing_failed' {
  return error instanceof PasswordGenerationError && error.code === 'empty-set'
    ? 'invalid_input'
    : 'processing_failed'
}

interface PasswordGeneratorFormProps {
  generateOverride?: (
    length: number,
    settings: PasswordSettings,
  ) => GeneratedPassword
}

export function PasswordGeneratorForm({
  generateOverride = generatePassword,
}: PasswordGeneratorFormProps) {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [state, dispatch] = useReducer(
    passwordGeneratorReducer,
    undefined,
    createPasswordGeneratorState,
  )
  const lengthCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const copyFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastAutoGenerationRevision = useRef<number | null>(null)
  const copyRequestRevision = useRef(0)
  const currentPassword = useRef('')
  const {
    draftLength,
    committedLength,
    settings,
    generated,
    copied,
    errorCode,
    generationRevision,
  } = state
  const password = generated?.value ?? ''
  currentPassword.current = password

  const preview = useMemo(() => {
    const sets = selectedCharacterSets(settings)
    const poolSize = sets.join('').length
    const entropyBits = poolSize > 0 ? draftLength * Math.log2(poolSize) : 0
    return {
      poolSize,
      selectedSetCount: sets.length,
      entropyBits,
      strength: strengthKey(entropyBits),
    }
  }, [draftLength, settings])

  const clearLengthCommitTimer = useCallback(() => {
    if (lengthCommitTimer.current !== null) {
      clearTimeout(lengthCommitTimer.current)
      lengthCommitTimer.current = null
    }
  }, [])

  const invalidateCopyRequest = useCallback(() => {
    copyRequestRevision.current += 1
    if (copyFeedbackTimer.current !== null) {
      clearTimeout(copyFeedbackTimer.current)
      copyFeedbackTimer.current = null
    }
    dispatch({ type: 'reset-copy' })
  }, [])

  const commitLength = useCallback((value: number) => {
    invalidateCopyRequest()
    clearLengthCommitTimer()
    dispatch({ type: 'commit-length', length: value })
  }, [clearLengthCommitTimer, invalidateCopyRequest])

  const handleSliderLengthChange = useCallback((value: number) => {
    invalidateCopyRequest()
    dispatch({ type: 'change-length', length: value })
    clearLengthCommitTimer()
    lengthCommitTimer.current = setTimeout(() => {
      lengthCommitTimer.current = null
      dispatch({ type: 'commit-length', length: value })
    }, LENGTH_COMMIT_DELAY_MS)
  }, [clearLengthCommitTimer, invalidateCopyRequest])

  const runGeneration = useCallback((
    length: number,
    generationSettings: PasswordSettings,
  ) => {
    invalidateCopyRequest()

    try {
      const nextPassword = runTxtifyToolOperation(
        TXTIFY_ANALYTICS_TOOLS.passwordGenerator,
        () => generateOverride(length, generationSettings),
        {
          presetMask: passwordPresetMask(generationSettings),
          errorCode: generationErrorCode,
        },
      )
      dispatch({ type: 'generation-succeeded', generated: nextPassword })
    } catch (err) {
      const code = err instanceof PasswordGenerationError ? err.code : 'crypto-unavailable'
      dispatch({ type: 'generation-failed', errorCode: code })
    }
  }, [generateOverride, invalidateCopyRequest])

  useEffect(() => {
    if (lastAutoGenerationRevision.current === generationRevision) {
      return
    }
    lastAutoGenerationRevision.current = generationRevision
    runGeneration(committedLength, settings)
  }, [committedLength, generationRevision, runGeneration, settings])

  useEffect(() => () => {
    copyRequestRevision.current += 1
    clearLengthCommitTimer()
    if (copyFeedbackTimer.current !== null) {
      clearTimeout(copyFeedbackTimer.current)
    }
  }, [clearLengthCommitTimer])

  const handleCopy = useCallback(async () => {
    if (!password) return
    const copiedPassword = password
    invalidateCopyRequest()
    const requestRevision = copyRequestRevision.current
    try {
      await navigator.clipboard.writeText(copiedPassword)
      if (
        copyRequestRevision.current !== requestRevision ||
        currentPassword.current !== copiedPassword
      ) {
        return
      }
      dispatch({ type: 'copy-succeeded' })
      if (copyFeedbackTimer.current !== null) {
        clearTimeout(copyFeedbackTimer.current)
      }
      copyFeedbackTimer.current = setTimeout(() => {
        if (copyRequestRevision.current !== requestRevision) return
        copyFeedbackTimer.current = null
        dispatch({ type: 'reset-copy' })
      }, 1500)
    } catch {
      if (copyRequestRevision.current !== requestRevision) return
      dispatch({ type: 'reset-copy' })
    }
  }, [invalidateCopyRequest, password])

  const error = errorCode === null
    ? null
    : errorCode === 'empty-set'
      ? t.passwordGenerator.emptySetError
      : t.passwordGenerator.cryptoError

  return (
    <>
      <Card className="overflow-hidden shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
          <div className="text-sm font-medium tracking-normal">
            {t.passwordGenerator.result}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                if (draftLength !== committedLength) {
                  commitLength(draftLength)
                  return
                }
                runGeneration(committedLength, settings)
              }}
            >
              {t.passwordGenerator.generate}
            </Button>
            {password && (
              <Button variant="outline" onClick={handleCopy}>
                {copied ? t.passwordGenerator.copied : t.passwordGenerator.copy}
              </Button>
            )}
          </div>
        </div>

        <div className="p-4">
          <div
            className={cn(
              'min-h-16 rounded-lg border bg-background p-4 font-mono text-base leading-7 tracking-normal',
              'break-all text-foreground',
              !password && 'text-muted-foreground',
            )}
            aria-live="polite"
            aria-label={t.passwordGenerator.result}
          >
            {password || t.passwordGenerator.resultPlaceholder}
          </div>
          {error && (
            <p className="mt-3 rounded-lg border border-destructive/35 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <p className="mt-3 text-xs leading-5 text-muted-foreground">
            {t.passwordGenerator.securityNote}
          </p>
        </div>
      </Card>

      <Card className="mt-4 overflow-hidden shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b bg-muted/45 px-4 py-3">
          <div className="text-sm font-medium tracking-normal">
            {t.passwordGenerator.controls}
          </div>
        </div>

        <div className="grid gap-6 p-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-6">
            <div>
              <div className="mb-2 flex items-center justify-between gap-4">
                <label
                  htmlFor="password-length"
                  className="text-sm font-medium tracking-normal"
                >
                  {t.passwordGenerator.length}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={MIN_PASSWORD_LENGTH}
                    max={MAX_PASSWORD_LENGTH}
                    value={draftLength}
                    onChange={(event) => {
                      commitLength(
                        parseInt(event.target.value, 10) || MIN_PASSWORD_LENGTH,
                      )
                    }}
                    className={cn(
                      'h-9 w-20 rounded-lg border bg-card px-3 text-sm tabular-nums',
                      'focus:outline-none focus:ring-2 focus:ring-primary/35',
                    )}
                    aria-label={t.passwordGenerator.length}
                  />
                  <span className="text-xs text-muted-foreground">
                    {t.passwordGenerator.lengthUnit}
                  </span>
                </div>
              </div>
              <input
                id="password-length"
                type="range"
                min={MIN_PASSWORD_LENGTH}
                max={MAX_PASSWORD_LENGTH}
                value={draftLength}
                onChange={(event) => {
                  handleSliderLengthChange(parseInt(event.target.value, 10))
                }}
                onPointerUp={(event) => {
                  commitLength(parseInt(event.currentTarget.value, 10))
                }}
                onKeyUp={(event) => {
                  commitLength(parseInt(event.currentTarget.value, 10))
                }}
                className="h-2 w-full cursor-pointer accent-primary"
              />
            </div>

            <div>
              <div className="mb-3 text-sm font-medium tracking-normal">
                {t.passwordGenerator.characterSets}
              </div>
              <div className="grid grid-cols-1 gap-2 min-[360px]:grid-cols-2 sm:gap-3">
                {CHARACTER_SET_ORDER.map((key) => (
                  <label
                    key={key}
                    className={cn(
                      'flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2 shadow-sm transition-colors',
                      settings[key] && 'border-primary/45 bg-primary/5',
                    )}
                  >
                    <span className="text-sm font-medium tracking-normal">
                      {characterSetLabel(key, t)}
                    </span>
                    <input
                      type="checkbox"
                      checked={settings[key]}
                      onChange={() => {
                        const enabledCount = CHARACTER_SET_ORDER.filter(
                          (candidate) => settings[candidate],
                        ).length
                        if (settings[key] && enabledCount === 1) {
                          rejectTxtifyToolOperation(
                            TXTIFY_ANALYTICS_TOOLS.passwordGenerator,
                            'invalid_input',
                            passwordPresetMask({
                              ...settings,
                              [key]: false,
                            }),
                          )
                        }
                        invalidateCopyRequest()
                        dispatch({ type: 'toggle-character-set', key })
                      }}
                      className="h-4 w-4 accent-primary"
                    />
                  </label>
                ))}
              </div>
            </div>

            <label
              className={cn(
                'flex cursor-pointer items-start justify-between gap-4 rounded-lg border bg-card p-4 shadow-sm transition-colors',
                settings.excludeAmbiguous && 'border-primary/45 bg-primary/5',
              )}
            >
              <span>
                <span className="block text-sm font-medium tracking-normal">
                  {t.passwordGenerator.excludeAmbiguous}
                </span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                  {t.passwordGenerator.excludeAmbiguousHint}
                </span>
              </span>
              <input
                type="checkbox"
                checked={settings.excludeAmbiguous}
                onChange={() => {
                  invalidateCopyRequest()
                  dispatch({ type: 'toggle-ambiguous' })
                }}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
              />
            </label>
          </div>

          <Card className="border-dashed bg-card/70 shadow-sm">
            <CardContent className="space-y-4 p-4">
              <div>
                <div className="text-xs font-medium uppercase text-muted-foreground">
                  {t.passwordGenerator.entropy}
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-3xl font-bold tabular-nums tracking-normal">
                    {Math.round(preview.entropyBits).toLocaleString(localeCode)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t.passwordGenerator.bits}
                  </span>
                </div>
              </div>

              <div
                className={cn(
                  'inline-flex rounded-md border px-2.5 py-1 text-xs font-medium',
                  strengthClass(preview.strength),
                )}
              >
                {t.passwordGenerator.strength[preview.strength]}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border bg-background p-3">
                  <div className="text-xs text-muted-foreground">
                    {t.passwordGenerator.poolSize}
                  </div>
                  <div className="mt-1 font-semibold tabular-nums">
                    {preview.poolSize.toLocaleString(localeCode)}
                  </div>
                </div>
                <div className="rounded-lg border bg-background p-3">
                  <div className="text-xs text-muted-foreground">
                    {t.passwordGenerator.stats.selectedSets}
                  </div>
                  <div className="mt-1 font-semibold tabular-nums">
                    {preview.selectedSetCount.toLocaleString(localeCode)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Card>
    </>
  )
}

export function PasswordGeneratorPage() {
  return (
    <ToolPageShell toolKey="passwordGenerator">
      <PasswordGeneratorForm />
    </ToolPageShell>
  )
}
